import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import duration from 'dayjs/plugin/duration'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'

// библиотека dayjs на самом деле не так важна
// утилитарная функция для того и существует, чтобы можно было не привязываться к реализации
dayjs.extend(localeData)
dayjs.extend(calendar)
dayjs.extend(utc)
dayjs.extend(duration)

export type CalendarFormat = {
  sameDay?: string
  nextDay?: string
  nextWeek?: string
  lastDay?: string
  lastWeek?: string
  sameElse?: string
}
type StringFormat = string
type DateFormat = StringFormat | CalendarFormat
type DateType = string | number | Date /* | Moment*/ // надо бы расширить до момента, пока еще существует

type UnitType = 'day' | 'month' | 'year' | 'hour' | 'minute' | 'second' | 'millisecond' | 'date'
type OpUnitType = UnitType | 'week'
export type ManipulateUnitType = Omit<OpUnitType, 'date'>

// функция заранее каррированная, чтобы можно было использоваться во flow
export function formatDate(format?: DateFormat, utc?: boolean) {
  return function _formatDate(date?: DateType): string {
    const dayjsDate = dayjs(date)
    const _date = utc ? dayjsDate.utc() : dayjsDate

    if (typeof format === 'object') {
      return _date.calendar(null, format)
    }

    return _date.format(format)
  }
}

// функция заранее каррированная, чтобы можно было использоваться во flow
export const addDate = (value: number, unit?: ManipulateUnitType) => {
  return (date: DateType): Date => {
    return dayjs(date).add(value, unit).toDate()
  }
}

export function setDate(value: number, unit: UnitType) {
  return function _setDate(date: DateType): Date {
    return dayjs(date).set(unit, value).toDate()
  }
}

export const isSameDate = (a: DateType, b: DateType, unit?: OpUnitType): boolean => {
  return dayjs(a).isSame(b, unit)
}

export const startOfDate = (unit: OpUnitType) => {
  return (date: DateType): Date => {
    return dayjs(date).startOf(unit).toDate()
  }
}

export const isAfterDate = (a: DateType, b: DateType, unit?: OpUnitType): boolean => {
  return dayjs(a).isAfter(b, unit)
}

export const isBeforeDate = (a: DateType, b: DateType, unit?: OpUnitType): boolean => {
  return dayjs(a).isBefore(b, unit)
}

export const toUnix = (date: Date | string) => {
  const parsedDate = typeof date === 'string' ? new Date(date) : date

  return Math.floor(parsedDate.getTime() / 1000)
}

// на данный момент не очень понимаю как работать с utc и Date из JS
export const UNSTABLE_utcOffset = (offset: number, isLocalDate: boolean = true) => {
  return (originDate: Date | string): Date => {
    const date = new Date(originDate)
    const timezoneOffset = date.getTimezoneOffset()
    const operation = isLocalDate ? 1 : -1
    const diff = (timezoneOffset + offset) * operation

    if (diff !== 0) {
      date.setTime(date.getTime() + diff * 60 * 1000)
    }

    return date
  }
}

/**
 * Возвращает 1 если первый день понедельник и 0 если первый день воскресенье
 */
export const getFirstDayOfWeek = () => dayjs().localeData().firstDayOfWeek()

export function formatDay(format?: StringFormat) {
  return function _formatDay(day: number): string {
    return dayjs().day(day).format(format)
  }
}

export function getDifferenceBetweenDates(date1: DateType, date2: DateType, unit?: OpUnitType) {
  return dayjs(date2).diff(dayjs(date1), unit)
}

/**
 * Возвращает из ISO строки только дату, например, 2024-07-15T21:00:00-0300 => 2024-07-15
 */
export function getDateFromISOString(date: string): string {
  return date.split('T')[0]
}

export function toISOString(date: Date) {
  return date.toISOString()
}

export function stripTimezoneFromISOString(date: string): string {
  // Если заканчивается на 'Z', то это UTC
  if (date.endsWith('Z')) {
    return date.replace(/Z$/, '')
  }

  // Ищем индекс разделителя часового пояса (+ или -)
  const timezoneOffsetIndex = date.search(/[+-]\d{2}(:?\d{2})?$/)

  if (timezoneOffsetIndex === -1) {
    return date
  }

  return date.substring(0, timezoneOffsetIndex)
}
